import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, UNSAFE_LocationContext, useLocation, useNavigate, useNavigationType, useOutlet, type Location } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import logoWhite from "@mivio/ui/assets/mivio-wordmark-white.svg?url";
import { businessHandle, rootApi } from "./api";
import { useAccess } from "./access";
import { useBusiness, useBusinesses } from "./business";
import { SaveBar, SaveBarProvider, ToastHost, useSaveBar } from "./editor";
import type { NavItem } from "./nav";
import { useNeeds } from "./needs";
import { SearchPalette } from "./search";
import { Avatar, BusinessMark, go, useSession } from "./session";
import { Icon, plural } from "./ui";
import { SectionContext } from "./section";

/**
 * The admin's frame: the dark top bar (logo, search or the save bar, the bell, the business and person menu),
 * the sidebar (a section shows its pages only while you are in it, the page you are on hanging off a line from the
 * section's icon), Settings at its foot opening as a sheet over the page you were on.
 */

/** Whether the page at `path` is `to` or one of its pages. */
export function isAt(path: string, to: string, also: readonly string[] = [], except: readonly string[] = []): boolean {
  if (to === "/") return path === "/";
  const under = (t: string) => path === t || path.startsWith(`${t}/`);
  return !except.some(under) && [to, ...also].some(under);
}

/** A waiting count: nothing at 0 or when the server does not send it. */
function Waiting({ waiting, counts }: { waiting?: { count: string; title: string }; counts?: Record<string, number> }) {
  const n = waiting ? counts?.[waiting.count] : undefined;
  if (!waiting || typeof n !== "number" || n <= 0) return null;
  return (
    <span className="nav__count nav__count--accent" title={waiting.title}>
      {n}
    </span>
  );
}

/** One section: its own row, then, while you are in it, its pages. */
function NavSection({ item, path, badge, counts }: { item: NavItem; path: string; badge?: ReactNode; counts?: Record<string, number> }) {
  const subs = item.subs ?? [];
  // The most particular page wins (a page the section leaves out belongs to none of its pages either).
  const current = subs.reduce((best, s, i) => (isAt(path, s.to, s.also, item.except) && (best < 0 || s.to.length > subs[best]!.to.length) ? i : best), -1);
  const here = current < 0 && isAt(path, item.to, item.also, item.except);
  const open = subs.length > 0 && (here || current >= 0);
  return (
    <>
      <Link to={item.to} className={here ? "is-active" : open ? "is-open" : undefined} aria-current={here ? "page" : undefined}>
        <item.icon />
        {item.label}
        {badge}
        <Waiting waiting={item.waiting} counts={counts} />
      </Link>
      {open &&
        subs.map((s, i) => {
          const on = i === current;
          return (
            <Link key={s.to} to={s.to} className={`nav__sub${on ? " is-active" : ""}${i === 0 ? " is-first" : ""}`} aria-current={on ? "page" : undefined}>
              {current >= 0 && i <= current && <span className={on ? "nav__elbow" : "nav__trail"} aria-hidden="true" />}
              {s.label}
              <Waiting waiting={s.waiting} counts={counts} />
            </Link>
          );
        })}
    </>
  );
}

/** The sidebar as this person may use it: a section whose own page they cannot open leads to its first page they can. */
function visible(items: readonly NavItem[], canOpen: (path: string) => boolean): NavItem[] {
  return items.flatMap((i) => {
    const subs = i.subs?.filter((s) => canOpen(s.to));
    if (canOpen(i.to)) return [subs ? { ...i, subs } : i];
    const first = subs?.[0];
    return first ? [{ ...i, to: first.to, subs }] : [];
  });
}

/** A page this person's role does not include. */
export function NoAccess() {
  return (
    <div className="page">
      <div className="no-access">
        <span className="no-access__icon" aria-hidden="true">
          <Icon.lock />
        </span>
        <h1>You don't have access to this page</h1>
        <p>Your role doesn't include this part of the business. Ask the owner or an administrator to add it in Settings › Roles.</p>
        <Link className="btn" to="/">
          Go to Home
        </Link>
      </div>
    </div>
  );
}

/** The search, or the save bar while a page has unsaved changes. */
function TopSearch() {
  const bar = useSaveBar();
  return <div className={`topbar__search${bar ? " has-savebar" : ""}`}>{bar ? <SaveBar bar={bar} /> : <SearchPalette />}</div>;
}

/** Closes a popover on a click outside it and on Esc. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

interface Note { id: number; kind: string; title: string; body: string; link: string | null; at: string; read: boolean }

/** The bell: what needs you, across every business you're in. */
function Bell() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const q = useQuery({ queryKey: ["notifications"], queryFn: () => rootApi<{ unread: number; notifications: Note[] }>("/notifications"), staleTime: 30_000, refetchInterval: 60_000 });
  const unread = q.data?.unread ?? 0;
  const toggle = () => {
    setOpen((v) => !v);
    if (!open && unread > 0) {
      void rootApi("/notifications/read", { method: "POST", json: { ids: "all" } })
        .then(() => qc.setQueryData(["notifications"], (d: { unread: number; notifications: Note[] } | undefined) => (d ? { ...d, unread: 0 } : d)))
        .catch(() => undefined);
    }
  };
  const follow = (link: string) => {
    setOpen(false);
    const here = businessHandle();
    if (here && (link === `/${here}` || link.startsWith(`/${here}/`))) nav(link.slice(here.length + 1) || "/");
    else go(link);
  };
  return (
    <div className="topbar__pop" ref={ref}>
      <button type="button" className="topbar__icon" aria-label={unread ? `Notifications, ${unread} new` : "Notifications"} aria-expanded={open} onClick={toggle}>
        <Icon.bell />
        {unread > 0 && <span className="topbar__dot">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="topbar__menu topbar__menu--wide" role="menu">
          <p>
            <strong>Notifications</strong>
            {unread ? plural(unread, "new one") : "What needs you, in every business"}
          </p>
          <hr />
          {(q.data?.notifications ?? []).length === 0 ? (
            <p className="topbar__empty">Nothing yet.</p>
          ) : (
            q.data!.notifications.slice(0, 12).map((n) => (
              <button key={n.id} type="button" role="menuitem" className={`topbar__note${n.read ? "" : " is-new"}`} onClick={() => (n.link ? follow(n.link) : setOpen(false))}>
                <span>
                  <strong>{n.title}</strong>
                  {n.body && <small>{n.body}</small>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** The business and the person: switch business (a full load, like changing store), your login, sign out. */
function BusinessMenu({ inBusiness }: { inBusiness: boolean }) {
  const { me, signOut } = useSession();
  const businesses = useBusinesses();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const here = businessHandle();
  const current = inBusiness ? businesses.data?.find((b) => b.handle === here) : undefined;
  const others = (businesses.data ?? []).filter((b) => b.handle !== here);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  if (!me) return null;
  const name = current?.name ?? me.account.name;
  return (
    <div className="topbar__pop" ref={ref}>
      <button type="button" className="topbar__user" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {current ? <BusinessMark name={current.name} /> : <Avatar name={me.account.name} />}
        <span className="hide-sm">{name}</span>
      </button>
      {open && (
        <div className="topbar__menu" role="menu">
          {current && (
            <>
              <p>
                <strong>{current.name}</strong>
                {current.role?.name ?? "Member"}
              </p>
              <hr />
            </>
          )}
          {others.map((b) => (
            <a key={b.handle} href={`/${b.handle}`} role="menuitem">
              <BusinessMark name={b.name} small />
              {b.name}
            </a>
          ))}
          {me.account.hq && (
            <a href="/new" role="menuitem">
              <Icon.plus /> Create a business
            </a>
          )}
          {(others.length > 0 || me.account.hq) && <hr />}
          <p>
            <strong>{me.account.name}</strong>
            {me.account.email}
          </p>
          {inBusiness ? (
            <Link to="/account" role="menuitem">
              <Icon.key /> Your login
            </Link>
          ) : (
            <a href="/account" role="menuitem">
              <Icon.key /> Your login
            </a>
          )}
          <hr />
          <button type="button" role="menuitem" onClick={() => void signOut().then(() => go("/sign-in"))}>
            <Icon.back /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

function TopBar({ onBurger, inBusiness, search }: { onBurger?: () => void; inBusiness: boolean; search: boolean }) {
  return (
    <header className="topbar">
      <div className="topbar__start">
        {onBurger && (
          <button type="button" className="topbar__burger" aria-label="Open navigation" onClick={onBurger}>
            <Icon.menu />
          </button>
        )}
        <a className="topbar__brand" href={inBusiness ? `/${businessHandle()}` : "/"}>
          <img src={logoWhite} alt="Mivio" width="74" height="18" />
        </a>
      </div>
      {search ? <TopSearch /> : <div />}
      <div className="topbar__actions">
        <Bell />
        <BusinessMenu inBusiness={inBusiness} />
      </div>
    </header>
  );
}

/**
 * Settings: a sheet that rises from the bottom and covers everything under the top bar. The page it was opened from
 * stays behind it, so closing (the cross, or Esc) slides the sheet away and you are where you were.
 */
function SettingsSheet({ back, children }: { back: string; children: ReactNode }) {
  const nav = useNavigate();
  const bar = useSaveBar();
  const loc = useLocation();
  const [closing, setClosing] = useState(false);
  const sheet = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  const close = useCallback(() => {
    if (bar || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      nav(back);
      return;
    }
    setClosing(true);
    timer.current = window.setTimeout(() => nav(back), 200);
  }, [bar, back, nav]);

  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    const root = document.documentElement;
    const was = root.style.overflow;
    root.style.overflow = "hidden";
    sheet.current?.focus({ preventScroll: true });
    return () => {
      root.style.overflow = was;
    };
  }, []);
  useEffect(() => {
    scroller.current?.scrollTo?.({ top: 0 });
  }, [loc.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector(".modal, .palette, .topbar__menu, .menu__list")) return;
      close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <>
      <div className={`sheet-backdrop${closing ? " is-closing" : ""}`} aria-hidden="true" onClick={close} />
      <div ref={sheet} className={`sheet${closing ? " is-closing" : ""}`} role="dialog" aria-modal="true" aria-label="Settings" tabIndex={-1}>
        <button type="button" className="sheet__close" aria-label="Close settings" title="Close (Esc)" onClick={close}>
          <Icon.close />
        </button>
        <div ref={scroller} className="sheet__scroll">
          {children}
        </div>
      </div>
    </>
  );
}

/** Inside a business. */
export function Shell() {
  return (
    <SaveBarProvider>
      <ShellInner />
    </SaveBarProvider>
  );
}

function ShellInner() {
  const loc = useLocation();
  const navType = useNavigationType();
  const outlet = useOutlet();
  const { navItems } = useBusiness();
  const { canOpen } = useAccess();
  const inSettings = isAt(loc.pathname, "/settings");
  // The page Settings was opened from, kept as it was (its own address included) behind the sheet.
  const behind = useRef<{ el: ReactNode; loc: Location; type: typeof navType } | null>(null);
  if (!inSettings) behind.current = { el: outlet, loc, type: navType };
  const shownLoc = inSettings ? (behind.current?.loc ?? loc) : loc;
  const shownType = inSettings ? (behind.current?.type ?? navType) : navType;
  const locValue = useMemo(() => ({ location: shownLoc, navigationType: shownType }), [shownLoc, shownType]);
  const backTo = behind.current ? `${behind.current.loc.pathname}${behind.current.loc.search}${behind.current.loc.hash}` : "/";
  const [open, setOpen] = useState(false);
  const items = visible(navItems, canOpen);
  const pageAllowed = inSettings || canOpen(loc.pathname);
  // Counts beside the sidebar's items, from Home's "Needs you": "approvals" (Mivio's own) and "<solution>.<key>".
  const needs = useNeeds();
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const n of needs.data ?? []) out[n.solution === "core" ? n.key : `${n.solution}.${n.key}`] = n.count;
    out.approvals ??= 0;
    return out;
  }, [needs.data]);

  useEffect(() => setOpen(false), [loc.pathname]);
  // The section this page sits in (the most particular match), for the icon before its title.
  const section = useMemo(() => {
    const hit = items.filter((n) => n.to !== "/" && isAt(shownLoc.pathname, n.to, n.also, n.except)).sort((a, b) => b.to.length - a.to.length)[0];
    return hit ? { icon: hit.icon, label: hit.label } : null;
  }, [items, shownLoc.pathname]);

  return (
    <div className="shell">
      <TopBar inBusiness search onBurger={() => setOpen((v) => !v)} />
      <div className="frame">
        {open && <button type="button" className="scrim" aria-label="Close navigation" onClick={() => setOpen(false)} />}
        <aside className={`sidebar${open ? " is-open" : ""}`}>
          <nav className="nav" aria-label="Business">
            {items.map((item) => (
              <NavSection key={item.label} item={item} path={shownLoc.pathname} counts={counts} />
            ))}
          </nav>
          <div className="sidebar__foot">
            <NavLink to="/settings" className={inSettings || isAt(loc.pathname, "/legal") ? "is-active" : ""}>
              <Icon.gear />
              Settings
            </NavLink>
          </div>
        </aside>
        <main className="main" id="main" inert={inSettings} aria-hidden={inSettings || undefined}>
          <UNSAFE_LocationContext.Provider value={locValue}>
            <SectionContext.Provider value={section}>{inSettings ? (behind.current?.el ?? null) : pageAllowed ? outlet : <NoAccess />}</SectionContext.Provider>
          </UNSAFE_LocationContext.Provider>
        </main>
      </div>
      {inSettings && <SettingsSheet back={backTo}>{outlet}</SettingsSheet>}
      <ToastHost />
    </div>
  );
}

/** Above any business: your login, a new business, the welcome when you're in none yet. */
export function OuterShell({ children }: { children: ReactNode }) {
  const loc = useLocation();
  return (
    <SaveBarProvider>
      <div className="shell">
        <TopBar inBusiness={false} search={false} />
        <div className="frame">
          <aside className="sidebar">
            <nav className="nav" aria-label="Mivio">
              <Link to="/" className={loc.pathname === "/" ? "is-active" : undefined}>
                <Icon.home />
                Home
              </Link>
              <Link to="/account" className={isAt(loc.pathname, "/account") ? "is-active" : undefined}>
                <Icon.key />
                Your login
              </Link>
            </nav>
          </aside>
          <main className="main" id="main">
            {children}
          </main>
        </div>
        <ToastHost />
      </div>
    </SaveBarProvider>
  );
}
